import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, FileSpreadsheet, Download, Upload, CheckCircle2, XCircle } from "lucide-react";
import { invalidateSKUCache } from "@/components/SKUMasterCombobox";

const EMPTY = { sku_id: "", sku_name: "", active: true };

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); } }, [open]);

  async function downloadTemplate() {
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/sku-master/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "sku_master_template.xlsx"; a.click();
  }
  async function doPreview() {
    if (!file) return toast.error("Upload an xlsx file");
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/sku-master/bulk-preview`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed"); setPreview(d);
    } catch (e) { toast.error(e.message); }
  }
  async function doApply() {
    if (!file) return; setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/sku-master/bulk-apply`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      toast.success(`Created ${d.created}, updated ${d.updated}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading">Bulk Upload · SKU Master</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Upsert SKUs by SKU ID. SKU Name is stored EXACTLY as in the file (no parsing). Existing SKU IDs are updated.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 items-end">
          <div className="col-span-2"><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="sku-bulk-file" /></div>
          <Button variant="outline" onClick={downloadTemplate} data-testid="sku-template"><Download className="h-4 w-4 mr-1" /> Template</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview} data-testid="sku-bulk-preview"><FileSpreadsheet className="h-4 w-4 mr-1" /> Preview</Button>
          <Button onClick={doApply} disabled={busy || !file} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sku-bulk-apply">
            <Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Apply"}
          </Button>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 border-b text-sm">
              {preview.count} rows · <b>{preview.items.filter((i) => i.action === "create").length}</b> new · <b>{preview.items.filter((i) => i.action === "update").length}</b> updates
              {preview.warnings?.length ? <span className="ml-3 text-amber-700">· {preview.warnings.length} warning(s)</span> : null}
            </div>
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 sticky top-0">
                  <tr><th className="p-2 text-left">Action</th><th className="p-2 text-left">SKU ID</th><th className="p-2 text-left">SKU Name</th><th className="p-2 text-left">Active</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {preview.items.map((it, i) => (
                    <tr key={i}>
                      <td className="p-2"><span className={`text-[10px] px-2 py-0.5 rounded border ${it.action === "create" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{it.action}</span></td>
                      <td className="p-2 mono">{it.sku_id}</td>
                      <td className="p-2">{it.sku_name}</td>
                      <td className="p-2 text-xs">{it.active ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.warnings?.length ? (
              <div className="p-3 bg-amber-50 border-t text-[11px] text-amber-800 max-h-24 overflow-auto">
                {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SKUMaster() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin"].includes(user?.role);
  const canDelete = ["super_admin", "admin"].includes(user?.role);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  async function load() { const { data } = await api.get("/sku-master"); setList(data); setSelected(new Set()); invalidateSKUCache(); }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => list.filter((s) => !search || (s.sku_id + " " + s.sku_name).toLowerCase().includes(search.toLowerCase())),
    [list, search],
  );

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((r) => r.id))); }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} SKU(s)?`)) return;
    try { const { data } = await api.post("/sku-master/bulk-delete", { ids: Array.from(selected) }); toast.success(`${data.deleted} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  function startNew() { setForm(EMPTY); setEditId(null); setOpen(true); }
  function startEdit(s) { setForm({ sku_id: s.sku_id, sku_name: s.sku_name, active: s.active }); setEditId(s.id); setOpen(true); }

  async function save() {
    try {
      const payload = { sku_id: (form.sku_id || "").trim(), sku_name: form.sku_name, active: !!form.active };
      if (!payload.sku_id) return toast.error("SKU ID is required");
      if (!payload.sku_name) return toast.error("SKU Name is required");
      if (editId) await api.patch(`/sku-master/${editId}`, payload);
      else await api.post("/sku-master", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function remove(s) {
    if (!window.confirm(`Delete SKU ${s.sku_id}?`)) return;
    try { await api.delete(`/sku-master/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="sku-master-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">SKU Master</h1>
          <p className="text-sm text-slate-500 mt-1">
            Canonical list of SKU IDs and names. SKU Name is stored exactly as entered/uploaded — no parsing or transformation.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {canDelete && selected.size > 0 && (
              <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="sku-bulk-delete">
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}
              </Button>
            )}
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="sku-bulk-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>
            <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-sku-btn"><Plus className="h-4 w-4 mr-1.5" /> New SKU</Button>
          </div>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by SKU ID or name…" className="pl-9" data-testid="sku-search" />
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {canDelete && <th className="p-3.5 w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="sku-select-all" /></th>}
                <th className="p-3.5 text-left">SKU ID</th>
                <th className="p-3.5 text-left">SKU Name</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 ? (
                <tr><td colSpan={canDelete ? 5 : 4} className="p-12 text-center text-slate-500">No SKUs yet. Use Bulk Upload to import from XLSX.</td></tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="table-row-hover">
                    {canDelete && <td className="p-3.5"><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`sku-sel-${s.sku_id}`} /></td>}
                    <td className="p-3.5 mono text-[12px] font-semibold text-slate-900">{s.sku_id}</td>
                    <td className="p-3.5 text-slate-900">{s.sku_name}</td>
                    <td className="p-3.5">
                      {s.active
                        ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Active</span>
                        : <span className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Inactive</span>}
                    </td>
                    <td className="p-3.5 text-right">
                      <div className="flex gap-1 justify-end">
                        {canEdit && <Button variant="ghost" size="icon" onClick={() => startEdit(s)} data-testid={`sku-edit-${s.sku_id}`}><Pencil className="h-4 w-4" /></Button>}
                        {canDelete && <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`sku-del-${s.sku_id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filtered.length > 0 && (
          <div className="p-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            Showing {filtered.length} of {list.length} SKU(s)
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="heading">{editId ? "Edit" : "New"} SKU</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">SKU ID must be unique. SKU Name is stored exactly as you type — no parsing.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div><Label>SKU ID</Label><Input value={form.sku_id} onChange={(e) => setForm({ ...form, sku_id: e.target.value })} placeholder="XDB-004" data-testid="sku-id-input" /></div>
            <div><Label>SKU Name</Label><Input value={form.sku_name} onChange={(e) => setForm({ ...form, sku_name: e.target.value })} placeholder="XTRA Detergent Bar 250g · MRP 20" data-testid="sku-name-input" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="sku-active-switch" />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="sku-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </div>
  );
}
